import React, { memo, Suspense, useCallback, useEffect, useMemo, useState } from 'react'
import { HStack } from '../../../../../ui/Stack'
import { Button, Modal, message } from 'antd'
import { useSelector } from 'react-redux'
import { ModalAddNote } from '../../../../../views/modal-add-notes-new'
import { createSession, getDeliveriesFormData } from '../../selectors'
import ReturnToStageButton from '../../../list/return-to-stage-button'
import {
  CardActions,
  useCardActions,
  CardActionConfig,
} from '../../../../../components/v2/card-actions'
import { HSteps } from '../../../../../ui/Steps'
import { EditOutlined, SyncOutlined, DownloadOutlined, CopyOutlined } from '@ant-design/icons'

interface DeliveriesFormsHeaderProps {
  id: string | number
  className?: string
  stage?: number
  stages?: any[]
  toOrder?: () => void
  toFinish?: () => void
  onExportToSchedex?: () => void
  onUnloadFromSchedex?: () => void
  onClone?: () => void
  onReturnStage?: () => void
  showTitle?: boolean
}

const titlesButton = {
  0: '',
  1: '',
  2: 'Запланировать',
  3: '',
  4: '',
  5: '',
}

export const DeliveriesFormsHeader: React.FC<DeliveriesFormsHeaderProps> = memo(
  (props: DeliveriesFormsHeaderProps) => {
    const {
      className,
      id,
      stage = 0,
      stages,
      toOrder,
      toFinish,
      onExportToSchedex,
      onUnloadFromSchedex,
      onClone,
      onReturnStage,
      showTitle = true,
    } = props
    const isDisabledButtons = id === 'new'
    const { sessionData } = useSelector(createSession)
    const deliveriesFormData = useSelector(getDeliveriesFormData)

    const [isOpenModalAddNote, setIsOpenModalAddNote] = useState<boolean>(false)

    const [isLockedSchedex, setIsLockedSchedex] = useState(false)
    const [schedexLockedTimer, setSchedexLockedTimer] = useState<number | null>(null)
    const [schedexRequestCount, setSchedexRequestCount] = useState<number>(0)
    const [isModalVisible, setIsModalVisible] = useState(false)

    useEffect(() => {
      const checkLockStatus = async () => {
        try {
          const response = await fetch('/api/schedex/lock/status')
          const data = await response.json()

        if (data.isLocked) {
          setIsLockedSchedex(true)
          setSchedexLockedTimer(data.timeout)
          setSchedexRequestCount(data.requestCount || 0)
        }
        } catch (error) {
          console.error('Ошибка проверки блокировки:', error)
          message.error('Ошибка проверки блокировки')
        }
      }

      checkLockStatus()

      const eventSource = new EventSource('/api/schedex/events/lock')

      eventSource.onmessage = event => {
        const data = JSON.parse(event.data)
        setIsLockedSchedex(data.isLocked)
        setSchedexRequestCount(data.requestCount || 0)

        if (data.isLocked && data.timeout) {
          setSchedexLockedTimer(data.timeout)
        } else {
          setSchedexLockedTimer(null)
        }
      }

      eventSource.onerror = () => {
        console.error('Ошибка подключения к SSE')
        eventSource.close()
      }

      return () => {
        eventSource.close()
      }
    }, [])

    useEffect(() => {
      let interval: ReturnType<typeof setInterval>

      if (isLockedSchedex && schedexLockedTimer && schedexLockedTimer > 0) {
        interval = setInterval(() => {
          setSchedexLockedTimer(prev => {
            if (prev && prev > 0) {
              return prev - 1
            } else {
              clearInterval(interval)
              setIsLockedSchedex(false)
              setSchedexRequestCount(0)
              setIsModalVisible(false)
              return 0
            }
          })
        }, 1000)
      }

      return () => clearInterval(interval)
    }, [isLockedSchedex, schedexLockedTimer])

    const handleNextStageOnClick = useCallback(async () => {
      if (toOrder) {
        await toOrder?.()
      }
    }, [toOrder])

    const onCloseAddNote = useCallback(() => {
      setIsOpenModalAddNote(false)
    }, [setIsOpenModalAddNote])

    const handleExportToSchedex = useCallback(() => {
      if (isLockedSchedex && schedexLockedTimer && schedexLockedTimer > 0) {
        setIsModalVisible(true)
      } else {
        Modal.confirm({
          title: 'Подтвердите действие',
          content: 'Вы уверены, что хотите переотправить заказ в Шедекс?',
          okText: 'Да',
          cancelText: 'Нет',
          onOk: async () => {
            await onExportToSchedex?.()
          },
        })
      }
    }, [onExportToSchedex, isLockedSchedex, schedexLockedTimer])

    const handleUnloadFromSchedex = useCallback(() => {
      Modal.confirm({
        title: 'Подтвердите действие',
        content: 'Вы уверены, что хотите перевыгрузить заказ из Шедекс?',
        okText: 'Да',
        cancelText: 'Нет',
        onOk: async () => {
          await onUnloadFromSchedex?.()
        },
      })
    }, [onUnloadFromSchedex])

    const handleClone = useCallback(() => {
      Modal.confirm({
        title: 'Подтвердите действие',
        content: 'Вы уверены, что хотите клонировать заказ?',
        okText: 'Да',
        cancelText: 'Нет',
        onOk: async () => {
          await onClone?.()
        },
      })
    }, [onClone])

    const handleReturnToStage = useCallback(() => {
      onReturnStage?.()
    }, [onReturnStage])

    const actionsConfig = useMemo((): CardActionConfig[] => {
      const actions: CardActionConfig[] = [
        /*{
        key: 'next-stage',
        label: titlesButton[stage] || '',
        type: 'primary',
        onClick: handleNextStageOnClick,
        isPrimary: true,
        disabled: isDisabledButtons,
        condition: Boolean(titlesButton[stage]),
        order: 1
      },*/
        {
          key: 'export-schedex',
          label: 'Переотправить в Шедекс',
          icon: <SyncOutlined />,
          onClick: handleExportToSchedex,
          condition: stage > 0,
          order: 10,
        },
        {
          key: 'unload-schedex',
          label: 'Перевыгрузить из Шедекс',
          icon: <DownloadOutlined />,
          onClick: handleUnloadFromSchedex,
          condition: stage > 1,
          order: 20,
        },
        {
          key: 'clone',
          label: 'Клонировать',
          icon: <CopyOutlined />,
          onClick: handleClone,
          condition: stage > 0,
          order: 30,
        },
        {
          key: 'add-note',
          label: 'Добавить заметку',
          icon: <EditOutlined />,
          onClick: () => setIsOpenModalAddNote(true),
          disabled: isDisabledButtons,
          condition: true,
          order: 40,
        },
      ]

      return actions
    }, [
      stage,
      isDisabledButtons,
      handleNextStageOnClick,
      handleExportToSchedex,
      handleUnloadFromSchedex,
      handleClone,
    ])

    const { primaryAction, secondaryActions } = useCardActions({ actions: actionsConfig })

    return (
      <>
        <HStack gap={'8'} className={'mapWrapper'} wrap>
          <div>
            {deliveriesFormData?.stages && (
              <HSteps
                items={Object.values(deliveriesFormData?.stages?.items)}
                current={deliveriesFormData?.stages?.current}
                history={deliveriesFormData?.stages?.history}
              />
            )}
          </div>
          <HStack gap={'8'} align={'center'} wrap>
            {stage > 0 && stages && !isDisabledButtons && (
              <ReturnToStageButton
                selectedOrders={[
                  {
                    id: Number(id),
                    stage: stages[stage].code,
                  },
                ]}
                stages={Object.values(stages)}
                onSuccess={handleReturnToStage}
              />
            )}
            <CardActions primaryAction={primaryAction} secondaryActions={secondaryActions} />
          </HStack>
        </HStack>

        <ModalAddNote
          entityId={Number(id)}
          entityType={'delivery'}
          isOpen={isOpenModalAddNote}
          onClose={onCloseAddNote}
        />

        <Modal
          title="Выгрузка в Шедекс недоступна"
          open={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          footer={[
            <Button key="close" onClick={() => setIsModalVisible(false)}>
              Закрыть
            </Button>,
          ]}
        >
          <p>Превышен лимит вызовов: лимит для загрузки заказов составляет 2 раза в 30 секунд.</p>
          {schedexRequestCount > 0 && (
            <p>
              Сделано запросов: <b>{schedexRequestCount}/2</b>
            </p>
          )}
          {schedexLockedTimer !== null && (
            <p>
              Попробуйте снова через <b>{schedexLockedTimer}</b> секунд.
            </p>
          )}
        </Modal>
      </>
    )
  },
)
